import React, { useState } from 'react';
import {
    McDialog,
    McTable,
    McIcon,
    McButton,
    McTag,
    McTooltip,
    McLoadingIndicator
} from '@maersk-global/mds-react-wrapper/components-core';
import { McRadio } from '@maersk-global/mds-react-wrapper/components-core/mc-radio';
import { ConfirmationDialog } from '../../Common/ConfirmationDialog';

interface QueryHistoryDialogProps {
    open: boolean;
    onClose: () => void;
    selectedQuery: any;
    history: any[];
    loading: boolean;
    canCreateVersions: boolean;
    onViewSql: (version: any) => void;
    onSetCurrentVersion: (versionNumber: string) => void;
    isSettingCurrentVersion: boolean;
    onCreateVersion: () => void;
}

export const QueryHistoryDialog: React.FC<QueryHistoryDialogProps> = ({
    open,
    onClose,
    selectedQuery,
    history,
    loading,
    canCreateVersions,
    onViewSql,
    onSetCurrentVersion,
    isSettingCurrentVersion,
    onCreateVersion
}) => {
    const [selectedVersionNumber, setSelectedVersionNumber] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Reset selection when dialog opens/closes or history changes
    React.useEffect(() => {
        if (open && history.length > 0) {
            const currentVersion = history.find(v => v.current || v.isCurrent);
            setSelectedVersionNumber(currentVersion?.versionNumber || null);
        } else {
            setSelectedVersionNumber(null);
        }
    }, [open, history]);

    const selectedVersionNumberRef = React.useRef(selectedVersionNumber);
    React.useEffect(() => { selectedVersionNumberRef.current = selectedVersionNumber; }, [selectedVersionNumber]);

    const handleSetCurrent = () => {
        if (isSettingCurrentVersion) return;
        const currentSelected = selectedVersionNumberRef.current;
        if (currentSelected) {
            setShowConfirmDialog(true);
        }
    };

    const handleConfirmSetCurrent = () => {
        const currentSelected = selectedVersionNumberRef.current;
        if (currentSelected) {
            onSetCurrentVersion(currentSelected);
            setShowConfirmDialog(false);
        }
    };

    const currentVersion = history.find(v => v.current || v.isCurrent);
    const canSetCurrent = selectedVersionNumber && selectedVersionNumber !== currentVersion?.versionNumber;

    return (
        <>
            <McDialog
                open={open}
                heading={`Version History for ${selectedQuery?.name}`}
                closed={onClose}
                width="1000px"
            >
                {/* Content truncated for brevity, assuming internal structure is correct until end of McDialog */}
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                        <McLoadingIndicator />
                    </div>
                ) : history.length > 0 ? (
                    <div>
                        {history.length === 1 && (
                            <div style={{ backgroundColor: '#e5f6fd', color: '#014361', padding: '12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #b8e7fb' }}>
                                This query has only one version. Create additional versions to be able to switch between them.
                            </div>
                        )}
                        {history.length > 1 && (
                            <div style={{ backgroundColor: '#edf7ed', color: '#1e4620', padding: '12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #c3e6cb', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <McIcon icon="check-circle-solid" />
                                <span>
                                    <strong>Version Management:</strong> Select a version using the radio buttons below, then click "Set as Current" to activate it.
                                </span>
                            </div>
                        )}
                        <McTable
                            data={history.map(v => ({ ...v, id: v.versionNumber }))}
                            columns={[
                                { id: 'selection', label: '' },
                                { id: 'version', label: 'Version' },
                                { id: 'description', label: 'Description' },
                                { id: 'createdInfo', label: 'Created' },
                                { id: 'actions', label: '' }
                            ]}
                            datakey="id"
                        >
                            {history.map((version) => (
                                <React.Fragment key={version.versionNumber}>
                                    <div slot={`${version.versionNumber}_selection`}>
                                        {canCreateVersions && history.length > 1 && (
                                            <McRadio
                                                name="version-selection"
                                                value={version.versionNumber}
                                                checked={selectedVersionNumber === version.versionNumber}
                                                change={() => setSelectedVersionNumber(version.versionNumber)}
                                                label=""
                                                hiddenlabel
                                            />
                                        )}
                                    </div>
                                    <div slot={`${version.versionNumber}_version`}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="mds-text--medium-bold">
                                                Version {version.versionNumber}
                                            </span>
                                            {(version.current || version.isCurrent) && (
                                                <McTag label="Current" appearance="success" fit="small" />
                                            )}
                                        </div>
                                    </div>
                                    <div slot={`${version.versionNumber}_description`}>
                                        <p className="mds-text--small-regular" style={{ margin: 0 }}>
                                            {version.changeDescription || version.changeNotes || 'No description provided'}
                                        </p>
                                    </div>
                                    <div slot={`${version.versionNumber}_createdInfo`}>
                                        <span className="mds-text--small-regular">
                                            {version.createdBy} on {version.createdAt ? new Date(version.createdAt).toLocaleString() : 'Unknown'}
                                        </span>
                                    </div>
                                    <div slot={`${version.versionNumber}_actions`}>
                                        <McTooltip>
                                            <McButton
                                                slot="trigger"
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                    onViewSql(version);
                                                }}
                                                appearance="primary"
                                                variant="plain"
                                            >
                                                <McIcon icon="file" />
                                            </McButton>
                                            <span slot="content">View SQL</span>
                                        </McTooltip>
                                    </div>
                                </React.Fragment>
                            ))}
                        </McTable>
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                        <span className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>
                            No version history available for this query.
                        </span>
                    </div>
                )}
                <McButton slot="secondaryAction" appearance="primary" variant="outlined" style={{ width: '160px' }} onClick={onClose}>
                    <McIcon icon="times" slot="prefix" />
                    Close
                </McButton>
                {canCreateVersions && canSetCurrent && (
                    <McButton
                        slot="secondaryAction"
                        appearance="primary"
                        variant="outlined"
                        onClick={handleSetCurrent}
                        disabled={isSettingCurrentVersion}
                        style={{ width: '180px' }}
                    >
                        {isSettingCurrentVersion ? (
                            <McLoadingIndicator slot="prefix" />
                        ) : (
                            <McIcon icon="check-circle" slot="prefix" />
                        )}
                        {isSettingCurrentVersion ? 'Setting...' : 'Set as Current'}
                    </McButton>
                )}
                {canCreateVersions && (
                    <McButton
                        slot="primaryAction"
                        appearance="primary"
                        onClick={() => {
                            onClose();
                            onCreateVersion();
                        }}
                        variant="filled"
                        style={{ width: '160px' }}
                    >
                        <McIcon icon="plus" slot="prefix" />
                        New Version
                    </McButton>
                )}
            </McDialog>

            <ConfirmationDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleConfirmSetCurrent}
                title="Confirm Version Switch"
                content={
                    <div>
                        <p>Are you sure you want to set version <strong>{selectedVersionNumber}</strong> as the current active version?</p>
                        <p style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--mds-color-neutral-weaker-text)' }}>This will immediately affect all users running this query without specifying a version number.</p>
                    </div>
                }
                confirmLabel="Set as Current"
            />
        </>
    );
};
